/**
 * ALTIORA Design System — Motion Library
 *
 * Centralised, reusable animation presets built on framer-motion.
 * Every preset automatically respects the user's OS-level
 * `prefers-reduced-motion` preference — no per-component logic needed.
 *
 * DO NOT write ad-hoc motion in pages. Import from here.
 *
 *   import { fadeUp, staggerChildren, EASE } from "@/components/altiora/motion";
 */
import { useReducedMotion } from "framer-motion";

// ── Timing tokens ──────────────────────────────────────────────────
export const DURATION = {
  instant: 0.001,
  micro:   0.15,   // hover, focus, small state changes
  fast:    0.25,   // buttons, tooltips, toasts
  base:    0.4,    // cards, panels, tabs
  slow:    0.7,    // hero, section reveals
  slower:  1.2,    // scroll-triggered narrative
};

// ── Easing curves ──────────────────────────────────────────────────
export const EASE = {
  // Signature landing easing — smooth, confident, institutional
  altiora:  [0.2, 0.7, 0.2, 1],
  // Standard material-in-out
  standard: [0.4, 0, 0.2, 1],
  // Bounce-out (for celebratory moments only)
  spring:   [0.34, 1.56, 0.64, 1],
};

// ── Motion presets — spread with {...preset} ───────────────────────
export const fadeUp = {
  initial:     { opacity: 0, y: 18 },
  animate:     { opacity: 1, y: 0 },
  whileInView: { opacity: 1, y: 0 },
  viewport:    { once: true, margin: "-60px" },
  transition:  { duration: DURATION.slow, ease: EASE.altiora },
};

export const fadeIn = {
  initial:     { opacity: 0 },
  animate:     { opacity: 1 },
  whileInView: { opacity: 1 },
  viewport:    { once: true },
  transition:  { duration: DURATION.base, ease: EASE.standard },
};

export const slideInRight = {
  initial:     { opacity: 0, x: 24 },
  animate:     { opacity: 1, x: 0 },
  transition:  { duration: DURATION.base, ease: EASE.altiora },
};

export const slideInLeft = {
  initial:     { opacity: 0, x: -24 },
  animate:     { opacity: 1, x: 0 },
  transition:  { duration: DURATION.base, ease: EASE.altiora },
};

export const scaleIn = {
  initial:     { opacity: 0, scale: 0.96 },
  animate:     { opacity: 1, scale: 1 },
  exit:        { opacity: 0, scale: 0.96 },
  transition:  { duration: DURATION.fast, ease: EASE.altiora },
};

// Modal / dialog motion
export const modalOverlay = {
  initial:     { opacity: 0 },
  animate:     { opacity: 1 },
  exit:        { opacity: 0 },
  transition:  { duration: DURATION.fast, ease: EASE.standard },
};

export const modalPanel = {
  initial:     { opacity: 0, y: 12, scale: 0.98 },
  animate:     { opacity: 1, y: 0,  scale: 1 },
  exit:        { opacity: 0, y: 8,  scale: 0.98 },
  transition:  { duration: DURATION.fast, ease: EASE.altiora },
};

// Panel / drawer slide-in from right
export const drawerRight = {
  initial:     { x: "100%" },
  animate:     { x: 0 },
  exit:        { x: "100%" },
  transition:  { duration: DURATION.base, ease: EASE.altiora },
};

// Menu / dropdown
export const menuPop = {
  initial:     { opacity: 0, y: -4, scale: 0.98 },
  animate:     { opacity: 1, y: 0,  scale: 1 },
  exit:        { opacity: 0, y: -4, scale: 0.98 },
  transition:  { duration: DURATION.micro, ease: EASE.standard },
};

// Accordion expand/collapse
export const accordionExpand = {
  initial:     { height: 0, opacity: 0 },
  animate:     { height: "auto", opacity: 1 },
  exit:        { height: 0, opacity: 0 },
  transition:  { duration: DURATION.fast, ease: EASE.altiora },
};

// Table row entrance (used with staggerChildren container)
export const tableRow = {
  initial:     { opacity: 0, y: 6 },
  animate:     { opacity: 1, y: 0 },
  transition:  { duration: DURATION.fast, ease: EASE.altiora },
};

// Toast / notification
export const toastPop = {
  initial:     { opacity: 0, y: 12, scale: 0.98 },
  animate:     { opacity: 1, y: 0,  scale: 1 },
  exit:        { opacity: 0, y: 8,  scale: 0.98 },
  transition:  { duration: DURATION.fast, ease: EASE.altiora },
};

// Hover primitive — subtle lift (use with framer-motion whileHover)
export const hoverLift = {
  whileHover: { y: -2, transition: { duration: DURATION.micro, ease: EASE.standard } },
  whileTap:   { y:  0, transition: { duration: DURATION.instant } },
};

// Stagger container — for lists of cards/rows
export const staggerContainer = {
  animate: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
};

// Loading spinner rotation
export const spin = {
  animate:     { rotate: 360 },
  transition:  { duration: 1, repeat: Infinity, ease: "linear" },
};

// Route/page transitions — wrap <Routes> with <AnimatePresence mode="wait">
// then use `pageTransition` on each page's root motion.div.
export const pageTransition = {
  initial:    { opacity: 0, y: 8 },
  animate:    { opacity: 1, y: 0 },
  exit:       { opacity: 0, y: -6 },
  transition: { duration: DURATION.fast, ease: EASE.altiora },
};

// Skeleton shimmer (CSS-driven via @keyframes altiora-shimmer)
export const SKELETON_CSS = {
  background:
    "linear-gradient(90deg, #F1F5F9 0%, #E5E7EB 50%, #F1F5F9 100%)",
  backgroundSize: "800px 100%",
  animation:      "altiora-shimmer 1.6s linear infinite",
};

// ── Reduced-motion hook — always return safe variants ──────────────
export function useSafeMotion(preset) {
  const prefersReduced = useReducedMotion();
  if (!prefersReduced) return preset;
  // Strip Y/X/scale transforms; keep only opacity fades at 0 duration
  const strip = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    const { y, x, scale, rotate, ...rest } = obj;
    return rest;
  };
  return {
    ...preset,
    initial:    strip(preset.initial),
    animate:    strip(preset.animate),
    exit:       strip(preset.exit),
    transition: { ...preset.transition, duration: 0.001 },
  };
}

// ── ANIMATION_DIRECTORY — reference for the DesignSystem gallery ──
export const ANIMATION_DIRECTORY = [
  { id: "fadeUp",         fr: "Fade + montée",            usage: "Reveal de section, cartes" },
  { id: "fadeIn",         fr: "Fade simple",              usage: "Ambient / passif" },
  { id: "slideInRight",   fr: "Slide depuis la droite",   usage: "Détail latéral" },
  { id: "slideInLeft",    fr: "Slide depuis la gauche",   usage: "Navigation back" },
  { id: "scaleIn",        fr: "Zoom-in doux",             usage: "Modale, popover" },
  { id: "modalPanel",     fr: "Modale · panneau",         usage: "<Modal />" },
  { id: "drawerRight",    fr: "Drawer droite",            usage: "Panneau latéral" },
  { id: "menuPop",        fr: "Menu / dropdown",          usage: "Popover" },
  { id: "accordionExpand",fr: "Accordéon",                usage: "<Accordion />" },
  { id: "tableRow",       fr: "Ligne de tableau",         usage: "<Table />" },
  { id: "toastPop",       fr: "Notification",             usage: "sonner" },
  { id: "hoverLift",      fr: "Survol · élévation",       usage: "Card / bouton" },
  { id: "staggerContainer",fr: "Cascade enfants",         usage: "Liste" },
  { id: "spin",           fr: "Rotation loader",          usage: "<Loader />" },
];
